import { mutateCareRecords } from '../sourceLifecycle/sourceLifecycleService';
import { serverTimestamp } from 'firebase/firestore';
import { buildClearedTimelineDetails } from './timelineNoteRefreshPayloads';
import { createTimelineEditRevision } from './timelineRefreshRevision';
import { startTimelineNoteRefresh } from './timelineNoteRefreshService';

const DAILY_LOGS_COLLECTION = 'dailyLogs';
const DAILY_LOG_CATEGORY_ALLOWLIST = new Set([
  'mealtime',
  'mood',
  'sleep',
  'milestone',
  'challenge',
  'medical_note',
  'medication',
  'medication_change',
  'food',
  'bathroom',
  'health',
  'log',
  'behavior',
  'activity',
]);
const DAILY_LOG_CATEGORY_ALIASES = {
  daily_note: 'log',
  daily_log: 'log',
  dailyLog: 'log',
  meds: 'medication',
  toilet: 'bathroom',
};

const pad2 = (value) => String(value).padStart(2, '0');

export const formatDateForEditInput = (date = new Date()) => {
  const safeDate = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  return [
    safeDate.getFullYear(),
    pad2(safeDate.getMonth() + 1),
    pad2(safeDate.getDate()),
  ].join('-');
};

export const formatTimeForEditInput = (date = new Date()) => {
  const safeDate = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  return `${pad2(safeDate.getHours())}:${pad2(safeDate.getMinutes())}`;
};

export const parseTimelineEditDate = (value = '') => {
  const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const [, year, month, day] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day), 0, 0, 0, 0);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const parseTimelineEditTime = (value = '') => {
  const text = String(value || '').trim().toLowerCase();
  if (!text) {
    return null;
  }

  const match = text.match(/^(\d{1,2})(?::(\d{2}))?(?:\s*([ap])\.?m\.?)?$/i);
  if (!match) {
    return null;
  }

  let hours = Number(match[1]);
  const minutes = Number(match[2] || 0);
  const meridiem = match[3] || null;

  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || minutes < 0 || minutes > 59) {
    return null;
  }

  if (meridiem) {
    if (hours < 1 || hours > 12) {
      return null;
    }

    if (meridiem === 'p' && hours < 12) {
      hours += 12;
    } else if (meridiem === 'a' && hours === 12) {
      hours = 0;
    }
  } else if (hours < 0 || hours > 23) {
    return null;
  }

  return {
    hours,
    minutes,
    storageValue: `${pad2(hours)}:${pad2(minutes)}`,
  };
};

export const buildTimelineEditTimestamp = ({ localDate = '', localTime = '' } = {}) => {
  const date = parseTimelineEditDate(localDate);
  if (!date) return null;

  const timeParts = parseTimelineEditTime(localTime);
  if (timeParts) {
    date.setHours(timeParts.hours, timeParts.minutes, 0, 0);
  }

  return Number.isNaN(date.getTime()) ? null : date;
};

export const normalizeTimelineEditCategory = (category = '') => {
  const cleanCategory = String(category || '').trim();
  const aliasedCategory = DAILY_LOG_CATEGORY_ALIASES[cleanCategory] || cleanCategory;
  return DAILY_LOG_CATEGORY_ALLOWLIST.has(aliasedCategory) ? aliasedCategory : 'log';
};

export const updateTimelineNarrativeNote = async ({
  entryId,
  text,
  localDate,
  localTime,
  category,
  updatedBy = null,
  markReviewed = false,
  expectedRevision = null,
}) => {
  const cleanEntryId = String(entryId || '').trim();
  const cleanText = String(text || '').trim();

  if (!cleanEntryId) {
    throw new Error('Missing timeline entry id.');
  }

  if (!cleanText) {
    throw new Error('Add a note before saving.');
  }

  const timestamp = buildTimelineEditTimestamp({ localDate, localTime });
  if (!timestamp || (String(localTime || '').trim() && !parseTimelineEditTime(localTime))) {
    throw new Error('Choose a valid date and time.');
  }
  const entryDayKey = formatDateForEditInput(timestamp);
  const normalizedLocalTime = parseTimelineEditTime(localTime)?.storageValue || null;
  const cleanCategory = normalizeTimelineEditCategory(category);

  const updatePayload = {
    ...buildClearedTimelineDetails(),
    timelineEditRevision: createTimelineEditRevision(),
    text: cleanText,
    body: cleanText,
    content: cleanText,
    note: cleanText,
    notes: cleanText,
    timestamp,
    timestampUtc: timestamp,
    entryDate: timestamp.toDateString(),
    entryDayKey,
    localDate: entryDayKey,
    localTime: normalizedLocalTime,
    time: normalizedLocalTime,
    timeSource: normalizedLocalTime ? 'manual' : 'missing',
    timeConfidence: normalizedLocalTime ? 'exact' : null,
    'metadata.timeSource': normalizedLocalTime ? 'manual' : 'missing',
    'metadata.timeConfidence': normalizedLocalTime ? 'exact' : null,
    timeBucket: null,
    timeInferred: false,
    timeInferenceReason: null,
    updatedAt: serverTimestamp(),
    editedAt: serverTimestamp(),
    editState: 'edited',
  };

  if (updatedBy) {
    updatePayload.updatedBy = updatedBy;
    updatePayload.editedBy = updatedBy;
  }

  updatePayload.category = cleanCategory;

  if (markReviewed) {
    updatePayload.reviewConfidence = 'ready';
    updatePayload.reviewConfidenceLabel = 'Reviewed';
    updatePayload.reviewedAt = serverTimestamp();
    updatePayload.reviewedBy = updatedBy || null;
    updatePayload.reviewedVia = 'manual_edit';
    updatePayload['metadata.needsReview'] = false;
    updatePayload['metadata.captureFirstReviewState'] = 'reviewed';
    updatePayload['metadata.reviewConfidence'] = 'ready';
    updatePayload['metadata.reviewConfidenceLabel'] = 'Reviewed';
    updatePayload['metadata.reviewReasons'] = [];
    updatePayload['metadata.reviewedAt'] = serverTimestamp();
    updatePayload['metadata.reviewedBy'] = updatedBy || null;
    updatePayload['metadata.reviewedVia'] = 'manual_edit';
    updatePayload['metadata.captureFirst.needsReview'] = false;
    updatePayload['metadata.captureFirst.reviewLabel'] = 'Reviewed';
  }

  const result = await mutateCareRecords({ operation: 'edit', entry: { id: cleanEntryId, collection: DAILY_LOGS_COLLECTION },
    requestId: updatePayload.timelineEditRevision, ...(expectedRevision ? { expectedRevision } : {}),
    changes: { text: cleanText, category: cleanCategory, localDate: entryDayKey, localTime: normalizedLocalTime, timestamp: timestamp.toISOString(), timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, markReviewed } });
  updatePayload.timelineEditRevision = result.timelineEditRevision;
  updatePayload.sourceRevision = result.revisionId;

  return {
    id: cleanEntryId,
    ...updatePayload,
    timestamp,
    localDate: entryDayKey,
  };
};

export const saveTimelineNarrativeNoteAndReprocess = async ({
  childId,
  entryId,
  text,
  localDate,
  localTime,
  category,
  updatedBy = null,
  markReviewed = false,
  expectedRevision = null,
}) => {
  const updatedEntry = await updateTimelineNarrativeNote({
    entryId,
    text,
    localDate,
    localTime,
    category,
    updatedBy,
    markReviewed,
    expectedRevision,
  });

  const refreshQueued = await startTimelineNoteRefresh({ updatedEntry, childId, category, updatedBy });
  return { ...updatedEntry, refreshQueued, refreshedInBackground: refreshQueued };
};
